import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n, pickLocalized } from "@/lib/i18n";
import { fetchRecent, fetchSectors, fetchSiteStats } from "@/lib/api";
import SearchBox from "@/components/SearchBox";
import Seo from "@/components/Seo";
import { ArrowRight } from "lucide-react";

export default function Home() {
  const { t, locale } = useI18n();
  const [recent, setRecent] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [siteStats, setSiteStats] = useState(null);

  useEffect(() => {
    fetchRecent().then((d) => setRecent(d.brands || [])).catch(() => {});
    fetchSectors().then((d) => setSectors(d.sectors || [])).catch(() => {});
    fetchSiteStats().then((d) => setSiteStats(d)).catch(() => {});
  }, []);

  const homeJsonLd = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "BrandPassport",
    url: "https://brandpassport.ai",
    potentialAction: {
      "@type": "SearchAction",
      target: "https://brandpassport.ai/brands?q={search_term_string}",
      "query-input": "required name=search_term_string",
    },
  }), []);

  return (
    <>
      <Seo
        title={t.tagline}
        description={t.hero_sub}
        canonicalPath="/"
        locale={locale}
        jsonLd={homeJsonLd}
      />

      {/* Hero */}
      <section className="border-b" style={{ borderColor: "var(--bp-border)" }}>
        <div className="bp-container py-16 md:py-24">
          <div className="max-w-4xl">
            <div className="uppercase text-xs tracking-[0.3em] mb-6" style={{ color: "var(--bp-text-muted)" }}>
              {t.tagline}
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight" data-testid="hero-title">
              {t.hero_title}
            </h1>
            <p className="mt-6 text-base md:text-lg leading-relaxed max-w-2xl" style={{ color: "var(--bp-text-secondary)" }}>
              {t.hero_sub}
            </p>
            <div className="mt-10 max-w-2xl">
              <SearchBox large />
            </div>
            {siteStats && siteStats.brands_updated_last_7d > 0 && (
              <div
                className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 border text-xs"
                style={{ borderColor: "var(--bp-border)", background: "#F0FDF4", color: "#166534" }}
                data-testid="freshness-ribbon"
              >
                <span aria-hidden style={{ width: 6, height: 6, borderRadius: 999, background: "#22C55E" }} />
                <span className="font-mono">
                  {locale === "it"
                    ? `${siteStats.brands_updated_last_7d} brand aggiornati negli ultimi 7 giorni`
                    : `${siteStats.brands_updated_last_7d} brands updated in the last 7 days`}
                </span>
                {(() => {
                  const curr = siteStats.brands_updated_last_7d;
                  const prev = siteStats.brands_updated_previous_7d || 0;
                  if (prev === 0 && curr === 0) return null;
                  const delta = curr - prev;
                  if (delta === 0) return null;
                  const up = delta > 0;
                  return (
                    <span
                      className="inline-flex items-center gap-0.5 font-mono"
                      style={{ color: up ? "#166534" : "#B91C1C" }}
                      data-testid="freshness-ribbon-trend"
                      title={
                        locale === "it"
                          ? `Settimana precedente: ${prev}`
                          : `Previous week: ${prev}`
                      }
                    >
                      {up ? "↑" : "↓"} {Math.abs(delta)}
                    </span>
                  );
                })()}
                <span style={{ color: "var(--bp-text-muted)" }}>
                  · {siteStats.brands_public_total} {locale === "it" ? "totali" : "total"}
                </span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Sectors */}
      <section className="py-16 border-b" style={{ borderColor: "var(--bp-border)" }}>
        <div className="bp-container">
          <div className="flex items-baseline justify-between mb-8">
            <h2 className="font-serif text-3xl md:text-4xl">{t.section_sectors}</h2>
            <Link to="/brands" className="text-sm bp-link" data-testid="link-all-brands">{t.nav_brands} →</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {sectors.map((s) => {
              const cov = (siteStats?.sectors_coverage || []).find(
                (c) => c.code === s.code,
              );
              const total = cov?.total ?? s.total_indexed ?? 0;
              const published = cov?.published ?? 0;
              const pct = total > 0 ? Math.round((published / total) * 100) : 0;
              return (
                <Link
                  key={s.code}
                  to={`/sectors/${s.code.toLowerCase()}`}
                  className="group block p-8 bg-white border transition-colors hover:border-black"
                  style={{ borderColor: "var(--bp-border)" }}
                  data-testid={`sector-card-${s.code}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="uppercase text-xs tracking-widest mb-2" style={{ color: "var(--bp-text-muted)" }}>
                        Sector
                      </div>
                      <div className="font-serif text-3xl">{pickLocalized(s.name, locale)}</div>
                      <div className="mt-3 text-sm font-mono" style={{ color: "var(--bp-text-secondary)" }}>
                        {s.total_indexed} brands indexed · DPP deadline: {s.dpp_deadline_year}
                      </div>
                      {total > 0 && (
                        <div className="mt-4" data-testid={`sector-coverage-${s.code}`}>
                          <div className="flex items-baseline justify-between text-xs mb-1" style={{ color: "var(--bp-text-muted)" }}>
                            <span className="uppercase tracking-widest">
                              {locale === "it" ? "Con profilo pubblicato" : "With published profile"}
                            </span>
                            <span className="font-mono">
                              {published}/{total} · {pct}%
                            </span>
                          </div>
                          <div className="h-1.5 w-full" style={{ background: "#EEE" }}>
                            <div
                              className="h-full"
                              style={{
                                width: `${pct}%`,
                                background: pct >= 75 ? "#166534" : pct >= 33 ? "#CA8A04" : "#B45309",
                              }}
                            />
                          </div>
                          {/* 4-week per-sector trend line — item 4. Renders when
                              at least 2 daily snapshots exist for this sector. */}
                          {(() => {
                            const rows = (siteStats?.sectors_trend || [])
                              .map((r) => ({ at: r.at, entry: r.sectors?.[s.code] }))
                              .filter((r) => r.entry);
                            if (rows.length < 2) return null;
                            const values = rows.map((r) => r.entry.published || 0);
                            const maxV = Math.max(1, ...values);
                            const W = 120, H = 24, PAD = 2;
                            const step = (W - PAD * 2) / (values.length - 1);
                            const points = values
                              .map((v, i) => `${PAD + i * step},${H - PAD - (v / maxV) * (H - PAD * 2)}`)
                              .join(" ");
                            const first = rows[0].at?.slice(0, 10);
                            const last = rows[rows.length - 1].at?.slice(0, 10);
                            return (
                              <div className="mt-2 flex items-center gap-2" data-testid={`sector-trend-${s.code}`}>
                                <svg width={W} height={H} aria-hidden style={{ display: "block" }}>
                                  <polyline
                                    fill="none"
                                    stroke={pct >= 75 ? "#166534" : pct >= 33 ? "#CA8A04" : "#B45309"}
                                    strokeWidth="1.5"
                                    points={points}
                                  />
                                </svg>
                                <span className="text-[10px] font-mono" style={{ color: "var(--bp-text-muted)" }}>
                                  {first} → {last}
                                </span>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                    <ArrowRight className="ml-6 transition-transform group-hover:translate-x-1 shrink-0" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Recent */}
      <section className="py-16 border-b" style={{ borderColor: "var(--bp-border)" }}>
        <div className="bp-container">
          <div className="flex items-baseline justify-between mb-8">
            <h2 className="font-serif text-3xl md:text-4xl">{t.section_recent}</h2>
            <span className="text-xs font-mono uppercase tracking-widest" style={{ color: "var(--bp-text-muted)" }}>{t.chronological_note}</span>
          </div>
          {recent.length === 0 ? (
            <div
              className="border p-10 md:p-14 bg-white"
              style={{ borderColor: "var(--bp-border)" }}
              data-testid="home-empty-state"
            >
              <div className="uppercase text-xs tracking-[0.3em] mb-4" style={{ color: "var(--bp-text-muted)" }}>
                {t.empty_kicker}
              </div>
              <div className="font-serif text-3xl md:text-4xl leading-tight mb-4" style={{ color: "var(--bp-text)" }}>
                {t.empty_title}
              </div>
              <p className="text-base md:text-lg max-w-2xl leading-relaxed" style={{ color: "var(--bp-text-secondary)" }}>
                {t.empty_body}
              </p>
              <div className="mt-6 flex flex-wrap gap-6 text-sm">
                <Link to="/methodology" className="bp-link" data-testid="empty-link-methodology">{t.empty_cta_methodology} →</Link>
                <Link to="/for-brands" className="bp-link" data-testid="empty-link-brands">{t.empty_cta_for_brands} →</Link>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {recent.slice(0, 6).map((b) => (
                <Link
                  key={b.id}
                  to={`/brands/${b.slug}`}
                  className="block p-6 bg-white border hover:border-black transition-colors"
                  style={{ borderColor: "var(--bp-border)" }}
                  data-testid={`recent-${b.slug}`}
                >
                  <div className="text-xs font-mono uppercase tracking-widest" style={{ color: "var(--bp-text-muted)" }}>
                    {b.sector_code} · <time>{(b.updated_at || "").slice(0, 10)}</time>
                  </div>
                  <div className="font-serif text-2xl mt-2">{b.display_name}</div>
                  <div className="mt-3 text-sm" style={{ color: "var(--bp-text-secondary)" }}>
                    {t.publishes_of_seven(b.profile?.published_count ?? 0)}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* DPP Explainer */}
      <section className="py-16 border-b" style={{ borderColor: "var(--bp-border)" }}>
        <div className="bp-container max-w-4xl">
          <div className="uppercase text-xs tracking-[0.3em] mb-4" style={{ color: "var(--bp-text-muted)" }}>
            Regulation EU 2024/1781 · ESPR
          </div>
          <h2 className="font-serif text-3xl md:text-4xl mb-6">{t.section_dpp_title}</h2>
          <p className="text-base md:text-lg leading-relaxed" style={{ color: "var(--bp-text-secondary)" }}>
            {t.section_dpp_body}
          </p>
          <div className="mt-6 p-5 bg-white border-l-4 border-black" style={{ borderColor: "black" }}>
            <p className="text-sm md:text-base font-medium">{t.section_dpp_clarify}</p>
          </div>
          <div className="mt-6">
            <Link to="/dpp-explainer" className="bp-link text-sm" data-testid="link-dpp-explainer">{t.dpp_read_more}</Link>
          </div>
        </div>
      </section>
    </>
  );
}
